<template>
    <div>
        <app-template />
        <div class="exercise-library-page uk-container uk-container-large">
            <div class="container-fluid">
                <!-- Page Header -->
                <div class="page-header">
                    <div class="header-content">
                        <h2>
                            <i class="bi bi-journal-bookmark"></i>
                            Exercise Library
                        </h2>
                        <p class="subtitle">
                            Browse and manage exercises for training plans, sessions, and rehabilitation
                        </p>
                    </div>
                    <div v-if="canManageExercises" class="header-actions">
                        <button class="btn btn-sm btn-primary" @click="openAddExerciseModal()">
                            <i class="bi bi-plus-lg"></i>
                            Add Exercise
                        </button>
                        <button class="btn btn-sm" :class="adminMode ? 'btn-warning' : 'btn-outline-secondary'"
                            @click="adminMode = !adminMode" title="Show inactive exercises and activation controls">
                            <i class="bi bi-eye"></i>
                            {{ adminMode ? 'Hide Inactive' : 'Show Inactive' }}
                        </button>
                    </div>
                </div>

                <!-- Filters Section -->
                <div class="filters-section">
                    <div class="filter-row">
                        <!-- Category Filter -->
                        <div class="filter-group">
                            <label>Category</label>
                            <select v-model="selectedCategory" class="form-select form-select-sm">
                                <option value="all">All Categories</option>
                                <option v-for="cat in exerciseCategories" :key="cat.key" :value="cat.key">
                                    {{ cat.name }}
                                </option>
                            </select>
                        </div>

                        <!-- Type Filter -->
                        <div class="filter-group">
                            <label>Type</label>
                            <select v-model="selectedType" class="form-select form-select-sm">
                                <option value="all">All Types</option>
                                <option value="general">General Training</option>
                                <option value="rtp">RTP / Rehabilitation</option>
                            </select>
                        </div>

                        <!-- Difficulty Filter -->
                        <div class="filter-group">
                            <label>Difficulty</label>
                            <select v-model="selectedDifficulty" class="form-select form-select-sm">
                                <option value="all">All Levels</option>
                                <option value="easy">Easy</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="advanced">Advanced</option>
                            </select>
                        </div>

                        <!-- Search -->
                        <div class="filter-group search-group">
                            <label>Search</label>
                            <div class="search-input">
                                <i class="bi bi-search"></i>
                                <input v-model="searchQuery" type="text" class="form-control form-control-sm"
                                    placeholder="Search exercises...">
                                <button v-if="searchQuery" class="btn-clear" @click="searchQuery = ''">
                                    <i class="bi bi-x"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- RTP Phase Filter (only shown when RTP type selected) -->
                    <div v-if="selectedType === 'rtp'" class="phase-filter">
                        <label>RTP Phase</label>
                        <div class="phase-buttons">
                            <button v-for="phase in 5" :key="phase" class="phase-btn"
                                :class="{ active: selectedPhase === phase }" :style="selectedPhase === phase ? {
                                    backgroundColor: getPhaseColor(phase),
                                    borderColor: getPhaseColor(phase)
                                } : {}" @click="selectedPhase = selectedPhase === phase ? null : phase">
                                P{{ phase }}
                            </button>
                            <button v-if="selectedPhase" class="btn btn-sm btn-link" @click="selectedPhase = null">
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Admin Mode Notice -->
                <div v-if="adminMode" class="admin-notice">
                    <i class="bi bi-eye"></i>
                    Showing inactive exercises - you can reactivate or permanently hide them
                </div>

                <!-- Loading State -->
                <div v-if="loading" class="loading-state">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                    <span>Loading exercises...</span>
                </div>

                <!-- Exercise Grid -->
                <div v-else class="exercise-grid">
                    <!-- Category Sections -->
                    <div v-for="category in visibleCategories" :key="category.key" class="category-section">
                        <div class="category-header" @click="toggleCategory(category.key)">
                            <div class="category-title">
                                <i :class="category.icon"></i>
                                <h5>{{ category.name }}</h5>
                            </div>
                            <span class="exercise-count">
                                {{ getCategoryExercises(category.key).length }} exercises
                            </span>
                            <i
                                :class="expandedCategories.includes(category.key) ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
                        </div>

                        <transition name="slide">
                            <div v-if="expandedCategories.includes(category.key)" class="category-content">
                                <div v-if="getCategoryExercises(category.key).length === 0" class="no-exercises">
                                    <i class="bi bi-inbox"></i>
                                    <span>No exercises in this category</span>
                                </div>

                                <div v-else class="exercise-list">
                                    <div v-for="exercise in getCategoryExercises(category.key)" :key="exercise.id"
                                        class="exercise-card" :class="{ 'inactive': !exercise.is_active && adminMode }">
                                        <div class="exercise-header">
                                            <div class="exercise-info">
                                                <span class="exercise-name">{{ exercise.exercise_name }}</span>
                                                <div class="exercise-badges">
                                                    <span v-if="exercise.difficulty" class="badge difficulty-badge"
                                                        :class="'level-' + exercise.difficulty">
                                                        {{ exercise.progression_level || exercise.difficulty }}
                                                    </span>
                                                    <span v-if="exercise.phase_number" class="badge phase-badge">
                                                        Phase {{ exercise.phase_number }}
                                                    </span>
                                                    <span v-if="!exercise.is_active && adminMode"
                                                        class="badge inactive-badge">
                                                        Inactive
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="exercise-actions">
                                                <!-- Action Buttons - visible to all users with permissions -->
                                                <div v-if="canManageExercises" class="admin-actions">
                                                    <button class="btn-action btn-edit" title="Edit"
                                                        @click.stop="openEditExerciseModal(exercise)">
                                                        <i class="bi bi-pencil"></i>
                                                    </button>
                                                    <button class="btn-action btn-duplicate" title="Duplicate"
                                                        @click.stop="openDuplicateModal(exercise)">
                                                        <i class="bi bi-copy"></i>
                                                    </button>
                                                    <button v-if="adminMode" class="btn-action"
                                                        :class="exercise.is_active ? 'btn-deactivate' : 'btn-activate'"
                                                        :title="exercise.is_active ? 'Deactivate' : 'Activate'"
                                                        @click.stop="toggleExerciseActive(exercise)">
                                                        <i
                                                            :class="exercise.is_active ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                                                    </button>
                                                    <button class="btn-action btn-delete" title="Delete"
                                                        @click.stop="confirmDeleteExercise(exercise)">
                                                        <i class="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                                <button class="btn-expand" @click.stop="toggleExercise(exercise.id)"
                                                    :title="expandedExercises.includes(exercise.id) ? 'Hide details' : 'View details'">
                                                    <i
                                                        :class="expandedExercises.includes(exercise.id) ? 'bi bi-chevron-up' : 'bi bi-eye'"></i>
                                                </button>
                                            </div>
                                        </div>

                                        <!-- Expanded Details -->
                                        <transition name="fade">
                                            <div v-if="expandedExercises.includes(exercise.id)"
                                                class="exercise-details">
                                                <div v-if="exercise.description" class="detail-section">
                                                    <strong>Description</strong>
                                                    <p>{{ exercise.description }}</p>
                                                </div>

                                                <div class="detail-grid">
                                                    <div v-if="exercise.sets_reps" class="detail-item">
                                                        <i class="bi bi-arrow-repeat"></i>
                                                        <div>
                                                            <span class="detail-label">Sets/Reps</span>
                                                            <span class="detail-value">{{ exercise.sets_reps }}</span>
                                                        </div>
                                                    </div>
                                                    <div v-if="exercise.duration" class="detail-item">
                                                        <i class="bi bi-clock"></i>
                                                        <div>
                                                            <span class="detail-label">Duration</span>
                                                            <span class="detail-value">{{ exercise.duration }}</span>
                                                        </div>
                                                    </div>
                                                    <div v-if="exercise.intensity" class="detail-item">
                                                        <i class="bi bi-speedometer2"></i>
                                                        <div>
                                                            <span class="detail-label">Intensity</span>
                                                            <span class="detail-value">{{ exercise.intensity }}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div v-if="exercise.precautions" class="precautions-section">
                                                    <strong><i class="bi bi-shield-exclamation"></i>
                                                        Precautions</strong>
                                                    <p>{{ exercise.precautions }}</p>
                                                </div>

                                                <div class="media-links">
                                                    <a v-if="exercise.video_url" :href="exercise.video_url"
                                                        target="_blank" class="btn btn-outline-primary btn-sm">
                                                        <i class="bi bi-play-circle"></i> Watch Video
                                                    </a>
                                                    <a v-if="exercise.pdf_path" :href="getFilePath(exercise.pdf_path)"
                                                        target="_blank" class="btn btn-outline-secondary btn-sm">
                                                        <i class="bi bi-file-pdf"></i> View PDF
                                                    </a>
                                                </div>
                                            </div>
                                        </transition>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>

                    <!-- Empty State -->
                    <div v-if="filteredExercises.length === 0 && !loading" class="empty-state">
                        <i class="bi bi-search"></i>
                        <h5>No exercises found</h5>
                        <p>Try adjusting your filters or search query</p>
                        <button v-if="canManageExercises" class="btn btn-primary btn-sm"
                            @click="openAddExerciseModal()">
                            <i class="bi bi-plus-lg"></i> Add Exercise
                        </button>
                    </div>
                </div>

                <!-- Summary Footer -->
                <div v-if="!loading && filteredExercises.length > 0" class="summary-footer">
                    <span>Showing {{ filteredExercises.length }} of {{ exercises.length }} exercises</span>
                </div>
            </div>

            <!-- Add/Edit Exercise Modal -->
            <b-modal v-model="showExerciseModal" :title="editingExercise ? 'Edit Exercise' : 'Add New Exercise'"
                size="lg" scrollable hide-footer>
                <form @submit.prevent="saveExercise">
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Exercise Name <span class="text-danger">*</span></label>
                            <input v-model="exerciseForm.exercise_name" type="text" class="form-control" required>
                        </div>
                        <div class="col-md-3 mb-3">
                            <label class="form-label">Category <span class="text-danger">*</span></label>
                            <select v-model="exerciseForm.category" class="form-select" required>
                                <option v-for="cat in exerciseCategories" :key="cat.key" :value="cat.key">
                                    {{ cat.name }}
                                </option>
                            </select>
                        </div>
                        <div class="col-md-3 mb-3">
                            <label class="form-label">Difficulty</label>
                            <select v-model="exerciseForm.difficulty" class="form-select">
                                <option value="easy">Easy</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="advanced">Advanced</option>
                            </select>
                        </div>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Description</label>
                        <textarea v-model="exerciseForm.description" class="form-control" rows="3"></textarea>
                    </div>

                    <div class="row">
                        <div class="col-md-4 mb-3">
                            <label class="form-label">Sets/Reps</label>
                            <input v-model="exerciseForm.sets_reps" type="text" class="form-control"
                                placeholder="e.g., 3 x 10-12 reps">
                        </div>
                        <div class="col-md-4 mb-3">
                            <label class="form-label">Duration</label>
                            <input v-model="exerciseForm.duration" type="text" class="form-control"
                                placeholder="e.g., 15-20 mins">
                        </div>
                        <div class="col-md-4 mb-3">
                            <label class="form-label">Intensity</label>
                            <input v-model="exerciseForm.intensity" type="text" class="form-control"
                                placeholder="e.g., 50-60% HR max">
                        </div>
                    </div>

                    <!-- RTP Options -->
                    <div class="rtp-options mb-3">
                        <div class="form-check mb-2">
                            <input v-model="exerciseForm.is_rtp_exercise" type="checkbox" class="form-check-input"
                                id="isRtpExercise">
                            <label class="form-check-label" for="isRtpExercise">
                                This is an RTP / Rehabilitation exercise
                            </label>
                        </div>
                        <div v-if="exerciseForm.is_rtp_exercise" class="row">
                            <div class="col-md-6 mb-3">
                                <label class="form-label">RTP Phase</label>
                                <select v-model="exerciseForm.phase_number" class="form-select">
                                    <option :value="null">Not phase-specific</option>
                                    <option v-for="phase in 5" :key="phase" :value="phase">
                                        Phase {{ phase }} - {{ getPhaseName(phase) }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="form-label">Maturity Category</label>
                                <select v-model="exerciseForm.maturity_category" class="form-select">
                                    <option :value="null">All Players</option>
                                    <option value="Pre-PHV">Pre-PHV</option>
                                    <option value="Circa-PHV">Circa-PHV</option>
                                    <option value="Post-PHV">Post-PHV</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Precautions / Safety Notes</label>
                        <textarea v-model="exerciseForm.precautions" class="form-control" rows="2"
                            placeholder="Any safety warnings or special considerations..."></textarea>
                    </div>

                    <!-- Media Section -->
                    <div class="media-section mb-4">
                        <h6 class="section-title mb-3">
                            <i class="bi bi-file-earmark-media me-2"></i>Media & Attachments
                        </h6>
                        <div class="row">
                            <div class="col-md-12 mb-3">
                                <label class="form-label">Video URL (YouTube/Vimeo)</label>
                                <input v-model="exerciseForm.video_url" type="url" class="form-control"
                                    placeholder="https://www.youtube.com/watch?v=...">
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6 mb-3">
                                <label class="form-label">Exercise Image</label>
                                <div class="file-upload-box" @click="triggerFileInput('image')"
                                    @drop.prevent="handleFileDrop($event, 'image')" @dragover.prevent
                                    @dragenter.prevent>
                                    <div v-if="exerciseForm.image_path || imagePreview" class="file-preview">
                                        <img :src="imagePreview || getFilePath(exerciseForm.image_path)"
                                            alt="Exercise image" class="preview-image">
                                        <button type="button" class="btn-remove-file" @click.stop="removeFile('image')">
                                            <i class="bi bi-x-lg"></i>
                                        </button>
                                    </div>
                                    <div v-else class="upload-placeholder">
                                        <i class="bi bi-image"></i>
                                        <span>Click or drag image here</span>
                                        <small>JPG, PNG, GIF, WebP (max 10MB)</small>
                                    </div>
                                    <input type="file" ref="imageInput" @change="handleFileSelect($event, 'image')"
                                        accept="image/jpeg,image/png,image/gif,image/webp" class="d-none">
                                </div>
                                <div v-if="uploadingImage" class="upload-progress mt-2">
                                    <div class="spinner-border spinner-border-sm me-2"></div>
                                    <span>Uploading image...</span>
                                </div>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="form-label">PDF Instructions</label>
                                <div class="file-upload-box" @click="triggerFileInput('pdf')"
                                    @drop.prevent="handleFileDrop($event, 'pdf')" @dragover.prevent @dragenter.prevent>
                                    <div v-if="exerciseForm.pdf_path" class="file-preview file-preview-pdf">
                                        <i class="bi bi-file-earmark-pdf"></i>
                                        <span class="file-name">{{ getFileName(exerciseForm.pdf_path) }}</span>
                                        <button type="button" class="btn-remove-file" @click.stop="removeFile('pdf')">
                                            <i class="bi bi-x-lg"></i>
                                        </button>
                                    </div>
                                    <div v-else class="upload-placeholder">
                                        <i class="bi bi-file-earmark-pdf"></i>
                                        <span>Click or drag PDF here</span>
                                        <small>PDF files only (max 20MB)</small>
                                    </div>
                                    <input type="file" ref="pdfInput" @change="handleFileSelect($event, 'pdf')"
                                        accept="application/pdf" class="d-none">
                                </div>
                                <div v-if="uploadingPdf" class="upload-progress mt-2">
                                    <div class="spinner-border spinner-border-sm me-2"></div>
                                    <span>Uploading PDF...</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="d-flex justify-content-end gap-2 mt-4">
                        <button type="button" class="btn btn-secondary"
                            @click="showExerciseModal = false">Cancel</button>
                        <button type="submit" class="btn btn-primary"
                            :disabled="savingExercise || uploadingImage || uploadingPdf">
                            <span v-if="savingExercise">
                                <span class="spinner-border spinner-border-sm me-1"></span> Saving...
                            </span>
                            <span v-else>
                                <i class="bi bi-check-lg me-1"></i>
                                {{ editingExercise ? 'Update Exercise' : 'Add Exercise' }}
                            </span>
                        </button>
                    </div>
                </form>
            </b-modal>

            <!-- Duplicate Modal -->
            <b-modal v-model="showDuplicateModal" title="Duplicate Exercise" size="md" hide-footer>
                <p>Create a copy of <strong>{{ duplicatingExercise?.exercise_name }}</strong></p>
                <div class="mb-3">
                    <label class="form-label">New Exercise Name</label>
                    <input v-model="duplicateForm.new_name" type="text" class="form-control">
                </div>
                <div class="row">
                    <div class="col-md-6 mb-3">
                        <label class="form-label">Category</label>
                        <select v-model="duplicateForm.category" class="form-select">
                            <option v-for="cat in exerciseCategories" :key="cat.key" :value="cat.key">
                                {{ cat.name }}
                            </option>
                        </select>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label">Difficulty</label>
                        <select v-model="duplicateForm.difficulty" class="form-select">
                            <option value="easy">Easy</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                        </select>
                    </div>
                </div>
                <div class="d-flex justify-content-end gap-2 mt-3">
                    <button class="btn btn-secondary" @click="showDuplicateModal = false">Cancel</button>
                    <button class="btn btn-primary" :disabled="duplicatingExerciseLoading" @click="duplicateExercise">
                        <span v-if="duplicatingExerciseLoading">
                            <span class="spinner-border spinner-border-sm me-1"></span> Duplicating...
                        </span>
                        <span v-else><i class="bi bi-copy me-1"></i> Duplicate</span>
                    </button>
                </div>
            </b-modal>

            <!-- Delete Confirmation Modal -->
            <b-modal v-model="showDeleteModal" title="Delete Exercise" size="sm" hide-footer>
                <p>Are you sure you want to delete <strong>{{ deletingExercise?.exercise_name }}</strong>?</p>
                <p class="text-muted small">This will deactivate the exercise. It can be reactivated later if needed.
                </p>
                <div class="d-flex justify-content-end gap-2 mt-3">
                    <button class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
                    <button class="btn btn-danger" :disabled="deletingExerciseLoading" @click="deleteExercise">
                        <span v-if="deletingExerciseLoading">
                            <span class="spinner-border spinner-border-sm me-1"></span> Deleting...
                        </span>
                        <span v-else><i class="bi bi-trash me-1"></i> Delete</span>
                    </button>
                </div>
            </b-modal>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import AppTemplate from "../layouts/App";
import { ReturnToPlayService } from "@/services/rtp-service.js";

export default {
    name: "ExerciseLibrary",
    components: { AppTemplate },
    data() {
        return {
            loading: true,
            exercises: [],
            userData: null,

            // Filters
            selectedCategory: 'all',
            selectedType: 'all',
            selectedDifficulty: 'all',
            selectedPhase: null,
            searchQuery: '',

            // UI State
            adminMode: false,
            expandedCategories: ['strength', 'conditioning', 'technical'],
            expandedExercises: [],

            // Exercise Form Modal
            showExerciseModal: false,
            editingExercise: null,
            savingExercise: false,
            exerciseForm: this.getEmptyExerciseForm(),

            // File uploads
            uploadingImage: false,
            uploadingPdf: false,
            imagePreview: null,

            // Duplicate Modal
            showDuplicateModal: false,
            duplicatingExercise: null,
            duplicatingExerciseLoading: false,
            duplicateForm: {
                new_name: '',
                category: null,
                difficulty: null
            },

            // Delete Modal
            showDeleteModal: false,
            deletingExercise: null,
            deletingExerciseLoading: false,

            // Categories - Expanded for general use
            exerciseCategories: [
                { key: 'strength', name: 'Strength & Power', icon: 'bi bi-lightning-charge' },
                { key: 'conditioning', name: 'Conditioning & Fitness', icon: 'bi bi-heart-pulse' },
                { key: 'technical', name: 'Technical Skills', icon: 'bi bi-bullseye' },
                { key: 'tactical', name: 'Tactical & Positional', icon: 'bi bi-diagram-3' },
                { key: 'mobility', name: 'Mobility & Flexibility', icon: 'bi bi-arrows-move' },
                { key: 'recovery', name: 'Recovery & Regeneration', icon: 'bi bi-bandaid' },
                { key: 'warmup', name: 'Warm-up & Activation', icon: 'bi bi-thermometer-half' },
                { key: 'aerobic', name: 'Aerobic / Cardiovascular', icon: 'bi bi-activity' },
                { key: 'agility', name: 'Agility & Speed', icon: 'bi bi-wind' },
                { key: 'balance', name: 'Balance & Proprioception', icon: 'bi bi-pie-chart' },
                { key: 'sport_specific', name: 'Sport-Specific Drills', icon: 'bi bi-dribbble' },
                { key: 'contact', name: 'Contact & Match Practice', icon: 'bi bi-people' },
                { key: 'mental', name: 'Mental & Psychological', icon: 'bi bi-lightbulb' },
                { key: 'gym', name: 'Gym / Weight Room', icon: 'bi bi-building' },
            ],
        };
    },
    computed: {
        ...mapGetters(["getProfile"]),

        canManageExercises() {
            if (!this.userData) return false;
            const allowedRoles = ['Admin', 'Head Physio', 'Head Sports Science', 'Physio', 'Sports Science', 'HeadCoach', 'Coach'];
            return allowedRoles.includes(this.userData.userType) || allowedRoles.includes(this.userData.position);
        },

        filteredExercises() {
            let result = [...this.exercises];

            // Filter by category
            if (this.selectedCategory !== 'all') {
                result = result.filter(e => e.category === this.selectedCategory);
            }

            // Filter by type (RTP vs General)
            if (this.selectedType === 'rtp') {
                result = result.filter(e => e.phase_number !== null);
            } else if (this.selectedType === 'general') {
                result = result.filter(e => e.phase_number === null);
            }

            // Filter by difficulty
            if (this.selectedDifficulty !== 'all') {
                result = result.filter(e => e.difficulty === this.selectedDifficulty);
            }

            // Filter by RTP phase
            if (this.selectedPhase) {
                result = result.filter(e => e.phase_number === this.selectedPhase);
            }

            // Search
            if (this.searchQuery.trim()) {
                const query = this.searchQuery.toLowerCase();
                result = result.filter(e =>
                    e.exercise_name?.toLowerCase().includes(query) ||
                    e.description?.toLowerCase().includes(query) ||
                    e.category?.toLowerCase().includes(query)
                );
            }

            // Hide inactive unless in admin mode
            if (!this.adminMode) {
                result = result.filter(e => e.is_active !== false && e.is_active !== 0);
            }

            return result;
        },

        visibleCategories() {
            // Only show categories that have exercises (after filtering)
            return this.exerciseCategories.filter(cat =>
                this.getCategoryExercises(cat.key).length > 0 || this.adminMode
            );
        }
    },
    watch: {
        getProfile: {
            immediate: true,
            handler(profile) {
                if (profile) {
                    this.userData = profile;
                }
            }
        }
    },
    async mounted() {
        if (this.getProfile) {
            this.userData = this.getProfile;
        }
        await this.loadExercises();
    },
    methods: {
        async loadExercises() {
            this.loading = true;
            try {
                const params = {
                    include_inactive: this.adminMode
                };
                if (this.userData?.clubId) {
                    params.clubId = this.userData.clubId;
                }
                const response = await ReturnToPlayService.getExerciseLibrary(params);
                if (response.data.success) {
                    this.exercises = response.data.exercises || [];
                }
            } catch (error) {
                console.error("Error loading exercises:", error);
                this.$toast?.error("Failed to load exercises");
            } finally {
                this.loading = false;
            }
        },

        getEmptyExerciseForm() {
            return {
                exercise_name: '',
                category: 'strength',
                description: '',
                sets_reps: '',
                duration: '',
                intensity: '',
                difficulty: 'intermediate',
                precautions: '',
                video_url: '',
                image_path: '',
                pdf_path: '',
                phase_number: null,
                maturity_category: null,
                is_rtp_exercise: false,
                sort_order: 0
            };
        },

        toggleCategory(categoryKey) {
            const idx = this.expandedCategories.indexOf(categoryKey);
            if (idx > -1) {
                this.expandedCategories.splice(idx, 1);
            } else {
                this.expandedCategories.push(categoryKey);
            }
        },

        toggleExercise(exerciseId) {
            const idx = this.expandedExercises.indexOf(exerciseId);
            if (idx > -1) {
                this.expandedExercises.splice(idx, 1);
            } else {
                this.expandedExercises.push(exerciseId);
            }
        },

        getCategoryExercises(categoryKey) {
            return this.filteredExercises.filter(e => e.category === categoryKey);
        },

        getPhaseColor(phase) {
            const colors = {
                1: '#dc3545',
                2: '#fd7e14',
                3: '#ffc107',
                4: '#20c997',
                5: '#28a745'
            };
            return colors[phase] || '#6c757d';
        },

        getPhaseName(phase) {
            const names = {
                1: 'Acute/Rest',
                2: 'Light Aerobic',
                3: 'Sport-Specific',
                4: 'Non-Contact Training',
                5: 'Full Contact/Clearance'
            };
            return names[phase] || '';
        },

        // Modal Methods
        openAddExerciseModal() {
            this.editingExercise = null;
            this.exerciseForm = this.getEmptyExerciseForm();
            this.imagePreview = null;
            this.showExerciseModal = true;
        },

        openEditExerciseModal(exercise) {
            this.editingExercise = exercise;
            this.imagePreview = null;
            this.exerciseForm = {
                exercise_name: exercise.exercise_name || '',
                category: exercise.category || 'strength',
                description: exercise.description || '',
                sets_reps: exercise.sets_reps || '',
                duration: exercise.duration || '',
                intensity: exercise.intensity || '',
                difficulty: exercise.difficulty || 'intermediate',
                precautions: exercise.precautions || '',
                video_url: exercise.video_url || '',
                image_path: exercise.image_path || '',
                pdf_path: exercise.pdf_path || '',
                phase_number: exercise.phase_number || null,
                maturity_category: exercise.maturity_category || null,
                is_rtp_exercise: exercise.phase_number !== null,
                sort_order: exercise.sort_order || 0
            };
            this.showExerciseModal = true;
        },

        async saveExercise() {
            if (!this.exerciseForm.exercise_name) {
                this.$toast?.error('Exercise name is required');
                return;
            }

            this.savingExercise = true;
            try {
                const data = {
                    ...this.exerciseForm,
                    clubId: this.userData?.clubId || null,
                    phase_number: this.exerciseForm.is_rtp_exercise ? this.exerciseForm.phase_number : null
                };

                let response;
                if (this.editingExercise) {
                    data.id = this.editingExercise.id;
                    response = await ReturnToPlayService.updateExercise(data);
                } else {
                    response = await ReturnToPlayService.addExercise(data);
                }

                if (response.data.success) {
                    this.$toast?.success(this.editingExercise ? 'Exercise updated' : 'Exercise added');
                    this.showExerciseModal = false;
                    await this.loadExercises();
                } else {
                    this.$toast?.error(response.data.message || 'Failed to save exercise');
                }
            } catch (error) {
                console.error('Error saving exercise:', error);
                this.$toast?.error('Error saving exercise');
            } finally {
                this.savingExercise = false;
            }
        },

        openDuplicateModal(exercise) {
            this.duplicatingExercise = exercise;
            this.duplicateForm = {
                new_name: `${exercise.exercise_name} (Copy)`,
                category: exercise.category,
                difficulty: exercise.difficulty
            };
            this.showDuplicateModal = true;
        },

        async duplicateExercise() {
            if (!this.duplicateForm.new_name) {
                this.$toast?.error('Please enter a name');
                return;
            }

            this.duplicatingExerciseLoading = true;
            try {
                const response = await ReturnToPlayService.duplicateExercise(
                    this.duplicatingExercise.id,
                    this.userData?.clubId || null,
                    {
                        new_name: this.duplicateForm.new_name,
                        category: this.duplicateForm.category,
                        difficulty: this.duplicateForm.difficulty
                    }
                );

                if (response.data.success) {
                    this.$toast?.success('Exercise duplicated');
                    this.showDuplicateModal = false;
                    await this.loadExercises();
                } else {
                    this.$toast?.error(response.data.message || 'Failed to duplicate');
                }
            } catch (error) {
                console.error('Error duplicating:', error);
                this.$toast?.error('Error duplicating exercise');
            } finally {
                this.duplicatingExerciseLoading = false;
            }
        },

        confirmDeleteExercise(exercise) {
            this.deletingExercise = exercise;
            this.showDeleteModal = true;
        },

        async deleteExercise() {
            this.deletingExerciseLoading = true;
            try {
                const response = await ReturnToPlayService.deleteExercise(
                    this.deletingExercise.id,
                    this.userData?.clubId || null
                );

                if (response.data.success) {
                    this.$toast?.success('Exercise deleted');
                    this.showDeleteModal = false;
                    await this.loadExercises();
                } else {
                    this.$toast?.error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                console.error('Error deleting:', error);
                this.$toast?.error('Error deleting exercise');
            } finally {
                this.deletingExerciseLoading = false;
            }
        },

        async toggleExerciseActive(exercise) {
            try {
                const response = await ReturnToPlayService.toggleExerciseActive(
                    exercise.id,
                    this.userData?.clubId || null
                );

                if (response.data.success) {
                    const status = response.data.is_active ? 'activated' : 'deactivated';
                    this.$toast?.success(`Exercise ${status}`);
                    await this.loadExercises();
                } else {
                    this.$toast?.error(response.data.message || 'Failed to toggle status');
                }
            } catch (error) {
                console.error('Error toggling:', error);
                this.$toast?.error('Error toggling exercise status');
            }
        },

        // File Upload Methods
        triggerFileInput(type) {
            const refMap = { image: 'imageInput', pdf: 'pdfInput' };
            const inputRef = this.$refs[refMap[type]];
            if (inputRef) inputRef.click();
        },

        async handleFileSelect(event, type) {
            const files = event.target.files;
            if (!files || files.length === 0) return;
            await this.uploadFile(files[0], type);
            event.target.value = '';
        },

        async handleFileDrop(event, type) {
            const files = event.dataTransfer.files;
            if (!files || files.length === 0) return;
            await this.uploadFile(files[0], type);
        },

        async uploadFile(file, type) {
            const allowedTypes = {
                image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
                pdf: ['application/pdf']
            };
            const maxSizes = { image: 10 * 1024 * 1024, pdf: 20 * 1024 * 1024 };

            if (!allowedTypes[type].includes(file.type)) {
                this.$toast?.error(`Invalid file type for ${type}`);
                return;
            }

            if (file.size > maxSizes[type]) {
                this.$toast?.error(`File too large. Max ${maxSizes[type] / (1024 * 1024)}MB`);
                return;
            }

            if (type === 'image') this.uploadingImage = true;
            else this.uploadingPdf = true;

            try {
                const base64Data = await this.fileToBase64(file);
                const response = await ReturnToPlayService.uploadExerciseFile(
                    this.userData?.clubId || null,
                    file.name,
                    base64Data,
                    type
                );

                if (response.data.success) {
                    if (type === 'image') {
                        this.exerciseForm.image_path = response.data.file_path;
                        this.imagePreview = URL.createObjectURL(file);
                    } else {
                        this.exerciseForm.pdf_path = response.data.file_path;
                    }
                    this.$toast?.success(`${type} uploaded`);
                } else {
                    this.$toast?.error(response.data.message || `Failed to upload ${type}`);
                }
            } catch (error) {
                console.error(`Error uploading ${type}:`, error);
                this.$toast?.error(`Error uploading ${type}`);
            } finally {
                if (type === 'image') this.uploadingImage = false;
                else this.uploadingPdf = false;
            }
        },

        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },

        removeFile(type) {
            if (type === 'image') {
                this.exerciseForm.image_path = '';
                this.imagePreview = null;
            } else {
                this.exerciseForm.pdf_path = '';
            }
        },

        getFilePath(path) {
            if (!path) return '';
            if (path.startsWith('http')) return path;
            const baseUrl = window.location.href.indexOf('8080') > -1 ? 'http://localhost:8888/' : '/';
            return baseUrl + path;
        },

        getFileName(path) {
            if (!path) return '';
            const parts = path.split('/');
            const filename = parts[parts.length - 1];
            const idx = filename.indexOf('_');
            return idx > 0 && idx < 20 ? filename.substring(idx + 1) : filename;
        }
    }
};
</script>

<style scoped lang="scss">
.exercise-library-page {
    padding: 20px 0;
    min-height: 100vh;
    background: #f5f7fa;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    h2 {
        margin: 0;
        font-size: 24px;
        display: flex;
        align-items: center;
        gap: 10px;

        i {
            color: #007bff;
        }
    }

    .subtitle {
        margin: 4px 0 0;
        color: #6c757d;
        font-size: 14px;
    }
}

.header-actions {
    display: flex;
    gap: 10px;
}

.filters-section {
    background: white;
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.filter-row {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    align-items: flex-end;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 150px;

    label {
        font-size: 12px;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
    }

    &.search-group {
        flex: 1;
        min-width: 200px;
    }
}

.search-input {
    position: relative;

    i.bi-search {
        position: absolute;
        left: 10px;
        top: 50%;
        transform: translateY(-50%);
        color: #adb5bd;
    }

    input {
        padding-left: 32px;
        padding-right: 32px;
    }

    .btn-clear {
        position: absolute;
        right: 4px;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        color: #adb5bd;
        cursor: pointer;
        padding: 4px 8px;

        &:hover {
            color: #495057;
        }
    }
}

.phase-filter {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e9ecef;

    label {
        font-size: 12px;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
        margin-bottom: 8px;
        display: block;
    }
}

.phase-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
}

.phase-btn {
    padding: 6px 14px;
    border: 2px solid #e0e0e0;
    border-radius: 20px;
    background: white;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        border-color: #007bff;
    }

    &.active {
        color: white;
        border-color: transparent;
    }
}

.admin-notice {
    background: #fff3cd;
    color: #856404;
    padding: 10px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
}

.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 60px;
    color: #6c757d;
}

.exercise-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.category-section {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.category-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
        background: #f8f9fa;
    }
}

.category-title {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;

    i {
        font-size: 20px;
        color: #007bff;
    }

    h5 {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
    }
}

.exercise-count {
    font-size: 12px;
    color: #6c757d;
    padding: 3px 10px;
    background: #f1f3f4;
    border-radius: 12px;
}

.category-content {
    padding: 0 20px 20px;
}

.no-exercises {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 30px;
    color: #adb5bd;

    i {
        font-size: 32px;
    }
}

.exercise-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.exercise-card {
    background: #f8f9fa;
    border-radius: 10px;
    overflow: hidden;
    transition: all 0.2s;

    &:hover {
        background: #f1f3f4;
    }

    &.inactive {
        opacity: 0.6;

        .exercise-name {
            text-decoration: line-through;
        }
    }
}

.exercise-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
}

.exercise-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
}

.exercise-name {
    font-weight: 500;
    font-size: 14px;
}

.exercise-badges {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.badge {
    font-size: 10px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 10px;

    &.difficulty-badge {
        &.level-easy {
            background: #d4edda;
            color: #155724;
        }

        &.level-intermediate {
            background: #fff3cd;
            color: #856404;
        }

        &.level-advanced {
            background: #f8d7da;
            color: #721c24;
        }
    }

    &.phase-badge {
        background: #e3f2fd;
        color: #1565c0;
    }

    &.inactive-badge {
        background: #f8d7da;
        color: #721c24;
    }
}

.exercise-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.admin-actions {
    display: flex;
    gap: 4px;
    margin-right: 8px;

    button {
        color: #403436;
    }
}

.btn-action {
    width: 28px;
    height: 28px;
    border: 1px solid #dee2e6;
    background: white;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    padding: 0;

    &:hover {
        border-color: #007bff;
        color: #007bff;
    }

    &.btn-edit:hover {
        background: #e7f1ff;
    }

    &.btn-duplicate:hover {
        border-color: #17a2b8;
        color: #17a2b8;
        background: #d1ecf1;
    }

    &.btn-activate:hover {
        border-color: #28a745;
        color: #28a745;
        background: #d4edda;
    }

    &.btn-deactivate:hover {
        border-color: #ffc107;
        color: #856404;
        background: #fff3cd;
    }

    &.btn-delete:hover {
        border-color: #dc3545;
        color: #dc3545;
        background: #f8d7da;
    }

    i {
        font-size: 13px;
    }
}

.btn-expand {
    width: 32px;
    height: 32px;
    border: 2px solid #007bff;
    background: #e7f1ff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    color: #007bff;

    i {
        font-size: 14px;
    }

    &:hover {
        background: #007bff;
        color: white;
    }
}

.exercise-details {
    padding: 0 16px 16px;
    border-top: 1px solid #e9ecef;
}

.detail-section {
    margin-bottom: 14px;

    strong {
        display: block;
        font-size: 12px;
        color: #6c757d;
        margin-bottom: 4px;
    }

    p {
        margin: 0;
        font-size: 13px;
    }
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
    margin-bottom: 14px;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    background: white;
    border-radius: 8px;

    > i {
        font-size: 16px;
        color: #007bff;
        margin-top: 2px;
    }
}

.detail-label {
    display: block;
    font-size: 10px;
    color: #9e9e9e;
    text-transform: uppercase;
}

.detail-value {
    display: block;
    font-size: 13px;
    font-weight: 500;
}

.precautions-section {
    padding: 12px;
    background: #fff8e1;
    border-radius: 8px;
    margin-bottom: 14px;

    strong {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #f57c00;
        margin-bottom: 6px;
    }

    p {
        margin: 0;
        font-size: 13px;
        color: #5d4037;
    }
}

.media-links {
    display: flex;
    gap: 10px;

    .btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
    background: white;
    border-radius: 12px;

    i {
        font-size: 48px;
        color: #dee2e6;
        margin-bottom: 16px;
        display: block;
    }

    h5 {
        margin: 0 0 8px;
        color: #495057;
    }

    p {
        margin: 0 0 20px;
        color: #6c757d;
    }
}

.summary-footer {
    text-align: center;
    padding: 16px;
    color: #6c757d;
    font-size: 13px;
}

// Modal styles
.rtp-options {
    background: #f8f9fa;
    padding: 12px 16px;
    border-radius: 8px;
}

.media-section {
    background: #f8f9fa;
    border-radius: 10px;
    padding: 16px;
}

.section-title {
    font-size: 14px;
    font-weight: 600;
    color: #495057;
}

.file-upload-box {
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    background: white;
    min-height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        border-color: #007bff;
        background: #f8f9ff;
    }
}

.upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: #6c757d;

    i {
        font-size: 28px;
        color: #adb5bd;
    }

    span {
        font-size: 13px;
    }

    small {
        font-size: 11px;
        color: #adb5bd;
    }
}

.file-preview {
    position: relative;

    &.file-preview-pdf {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;

        i {
            font-size: 36px;
            color: #dc3545;
        }

        .file-name {
            font-size: 12px;
            color: #495057;
        }
    }
}

.preview-image {
    max-width: 100%;
    max-height: 120px;
    border-radius: 6px;
}

.btn-remove-file {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #dc3545;
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 11px;
}

.upload-progress {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #007bff;
}

// Transitions
.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    max-height: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

// Responsive
@media (max-width: 768px) {
    .filter-row {
        flex-direction: column;
    }

    .filter-group {
        width: 100%;
    }

    .page-header {
        flex-direction: column;
        align-items: stretch;
    }

    .header-actions {
        justify-content: flex-end;
    }
}
</style>
